import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { driversService } from '../../services/driversService';
import { AdminDriversResponse, ApplicationStatus, DriverState, AppState } from '../../types/drivers';
import './Page.css';

const Drivers: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [drivers, setDrivers] = useState<AdminDriversResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageSize = 20;

  // Read from URL params or use defaults
  const searchQuery = searchParams.get('search') || '';
  const statusParam = searchParams.get('status');
  const statusFilter = statusParam === ''
    ? null
    : (statusParam || ApplicationStatus.ACCEPTED) as ApplicationStatus;
  const stateFilter = searchParams.get('state') as DriverState | null;
  const currentPage = parseInt(searchParams.get('page') || '0', 10);

  // Helper functions to update URL params
  const updateSearchQuery = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const updateStatusFilter = (value: ApplicationStatus | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null) {
      newParams.set('status', ''); // Set to empty string for ALL
    } else {
      newParams.set('status', value);
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const updateStateFilter = (value: DriverState | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('state', value);
    } else {
      newParams.delete('state');
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const updatePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchDrivers();
  }, [searchQuery, statusFilter, stateFilter, currentPage]);

  const fetchDrivers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await driversService.getDrivers({
        searchQuery,
        page: currentPage,
        pageSize,
        applicationStatus: statusFilter,
        driverState: stateFilter
      });

      if (response.success && response.data) {
        setDrivers(response.data);
      } else {
        setError(response.error || 'Failed to fetch drivers');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const search = formData.get('search') as string;
    updateSearchQuery(search);
  };

  const handleStatusFilterChange = (filter: ApplicationStatus | null) => {
    updateStatusFilter(filter);
  };

  const handleStateFilterChange = (filter: DriverState | null) => {
    updateStateFilter(filter);
  };

  const handleRowClick = (driverId: number) => {
    navigate(`/drivers/${driverId}`);
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  const getStatusColor = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return '#ffc107';
      case ApplicationStatus.ACCEPTED:
        return '#28a745';
      case ApplicationStatus.BLOCKED:
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusDisplay = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return t('restaurants.statusFilter.pending');
      case ApplicationStatus.ACCEPTED:
        return t('restaurants.statusFilter.accepted');
      case ApplicationStatus.BLOCKED:
        return t('restaurants.statusFilter.blocked');
      default:
        return status;
    }
  };

  const getDriverStateDisplay = (state: DriverState): string => {
    return state === DriverState.ACTIVE ? t('common.active') : t('common.inactive');
  };

  const getDriverStateColor = (state: DriverState): string => {
    return state === DriverState.ACTIVE ? '#28a745' : '#6c757d';
  };

  const getAppStateDisplay = (appState: AppState): string => {
    return appState === AppState.FOREGROUND ? '🟢 Online' : '🔴 Offline';
  };

  const getIsWorkingDisplay = (isWorking: boolean): string => {
    return isWorking ? t('drivers.working') : t('drivers.notWorking');
  };

  const getIsWorkingColor = (isWorking: boolean): string => {
    return isWorking ? '#28a745' : '#6c757d';
  };

  return (
    <div className="page">
      <div className="page-header with-actions">
        <div>
          <h1>{t('drivers.title')}</h1>
          <p>{t('drivers.subtitle')}</p>
        </div>
        <div className="page-header-actions">
          <button
            type="button"
            className="header-action-button"
            onClick={() => navigate('/driver-groups')}
          >
            {t('driverGroups.manageButton')}
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Search Section */}
        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-controls">
              <input
                type="text"
                name="search"
                placeholder={t('drivers.searchDrivers')}
                defaultValue={searchQuery}
                className="search-input"
              />
              <button type="submit" className="search-button">
                {t('common.search')}
              </button>
            </div>
          </form>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <h3>{t('drivers.applicationStatus')}</h3>
          <div className="filter-tags">
            <button
              className={`filter-tag ${statusFilter === null ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange(null)}
            >
              {t('restaurants.statusFilter.all')}
            </button>
            <button
              className={`filter-tag ${statusFilter === ApplicationStatus.PENDING ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange(ApplicationStatus.PENDING)}
            >
              {t('restaurants.statusFilter.pending')}
            </button>
            <button
              className={`filter-tag ${statusFilter === ApplicationStatus.ACCEPTED ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange(ApplicationStatus.ACCEPTED)}
            >
              {t('restaurants.statusFilter.accepted')}
            </button>
            <button
              className={`filter-tag ${statusFilter === ApplicationStatus.BLOCKED ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange(ApplicationStatus.BLOCKED)}
            >
              {t('restaurants.statusFilter.blocked')}
            </button>
          </div>

          <h3 style={{ marginTop: '1rem' }}>{t('drivers.driverState')}</h3>
          <div className="filter-tags">
            <button
              className={`filter-tag ${stateFilter === null ? 'active' : ''}`}
              onClick={() => handleStateFilterChange(null)}
            >
              {t('common.all')}
            </button>
            <button
              className={`filter-tag ${stateFilter === DriverState.ACTIVE ? 'active' : ''}`}
              onClick={() => handleStateFilterChange(DriverState.ACTIVE)}
            >
              {t('common.active')}
            </button>
            <button
              className={`filter-tag ${stateFilter === DriverState.PASSIVE ? 'active' : ''}`}
              onClick={() => handleStateFilterChange(DriverState.PASSIVE)}
            >
              {t('common.inactive')}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Drivers Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('drivers.loadingDrivers')}</div>
          ) : drivers.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('drivers.driverId')}</th>
                  <th>{t('drivers.fullName')}</th>
                  <th>{t('common.phone')}</th>
                  <th>App State</th>
                  <th>{t('drivers.isWorking')}</th>
                  <th>{t('drivers.driverState')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('drivers.balance')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={8} className="no-data">
                    {t('drivers.noDrivers')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('drivers.driverId')}</th>
                  <th>{t('drivers.fullName')}</th>
                  <th>{t('common.phone')}</th>
                  <th>App State</th>
                  <th>{t('drivers.isWorking')}</th>
                  <th>{t('drivers.driverState')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('drivers.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driverData) => (
                  <tr
                    key={driverData.driver.id}
                    onClick={() => handleRowClick(driverData.driver.id)}
                    className="clickable-row"
                  >
                    <td>{driverData.driver.id}</td>
                    <td>{driverData.driver.fullName}</td>
                    <td>{driverData.driver.phoneNumber}</td>
                    <td>{getAppStateDisplay(driverData.driver.appState)}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getIsWorkingColor(driverData.driver.isWorking) }}
                      >
                        {getIsWorkingDisplay(driverData.driver.isWorking)}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getDriverStateColor(driverData.driverState) }}
                      >
                        {getDriverStateDisplay(driverData.driverState)}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(driverData.driver.applicationStatus) }}
                      >
                        {getStatusDisplay(driverData.driver.applicationStatus)}
                      </span>
                    </td>
                    <td>{formatPrice(driverData.driver.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && drivers.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">{t('orders.page')} {currentPage + 1}</span>
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage + 1)}
              disabled={drivers.length < pageSize}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Drivers;
